import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Briefcase, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import { createArticleSchema } from "@/utils/schemaHelpers";

const HoroscopoVirgo = () => {
  const articleSchema = createArticleSchema(
    "Virgo: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Virgo. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de agosto y el 22 de septiembre.",
    `${window.location.origin}/horoscopo-virgo`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Virgo",
        "item": `${window.location.origin}/horoscopo-virgo`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Virgo: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Virgo. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de agosto y el 22 de septiembre." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-virgo`} />
        <meta property="og:title" content="Virgo: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Virgo. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-virgo`} />
        <meta property="og:type" content="article" />
      </Helmet>
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <div className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♍</div>
              <h1 className="text-3xl font-bold mb-4 text-glow">
                Virgo: Horóscopo Diario, Características y Compatibilidad con los 12 Signos
              </h1>
              <p className="text-xl text-muted-foreground">23 de Agosto - 22 de Septiembre</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Virgo: Predicciones Actualizadas</h2>
              
              <Card className="p-8 gradient-celestial">
                <Tabs defaultValue="hoy" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8">
                    <TabsTrigger value="hoy">Hoy</TabsTrigger>
                    <TabsTrigger value="semanal">Semanal</TabsTrigger>
                    <TabsTrigger value="mensual">Mensual</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="hoy" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Heart className="w-6 h-6 text-primary" />
                        Horóscopo de Hoy para Virgo
                      </h3>
                      <p className="text-center text-sm text-muted-foreground mb-6">Número: 3 | Color: Azul Marino</p>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-primary">💖 Amor</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La atención al detalle mejora tu relación. Pequeños actos de servicio pueden ser muy valiosos. Si buscas amor, presta atención a la rutina, allí puede estar.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-secondary">💼 Trabajo</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La precisión y la organización te llevarán al éxito. Es un día ideal para pulir documentos o abordar tareas que requieran un análisis meticuloso. Tu ética laboral es tu mejor carta.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-accent">✨ Salud</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Establece una rutina de bienestar práctica y realista. Revisa tu dieta y tus hábitos diarios. El yoga o la meditación te ayudarán a calmar tu mente analítica.
                          </p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-accent">💰 Dinero</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            La organización de tus finanzas es prioritaria. Revisa tu presupuesto, elimina gastos innecesarios y planifica con detalle tus ahorros. La eficiencia se traduce directamente en ganancias.
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="semanal" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Briefcase className="w-6 h-6 text-secondary" />
                        Horóscopo Semanal: Tu Guía para los Próximos 7 Días
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Una semana productiva llena de oportunidades para perfeccionar y mejorar. Los primeros días favorecen proyectos de organización y optimización de procesos. El fin de semana es ideal para cuidado personal y revisión de metas.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Mercurio te favorece con claridad mental y comunicación efectiva. Tu habilidad para el análisis detallado será invaluable. Confía en tu capacidad para mejorar cualquier situación con tu enfoque metódico y práctico.
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="mensual" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-accent" />
                        Horóscopo Mensual Virgo: Enfoque y Oportunidades Clave
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Este mes marca un período de refinamiento y crecimiento profesional. La primera mitad favorece proyectos que requieren atención al detalle y excelencia técnica. Tu habilidad para el servicio y la mejora continua será especialmente reconocida.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        La segunda mitad del mes es ideal para la salud, la rutina diaria y establecer sistemas que soporten tu bienestar a largo plazo. Es momento de aplicar tu perfeccionismo de manera que te sirva sin agobiarte. Encuentra el equilibrio entre excelencia y aceptación.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Virgo: El Sirviente Analítico</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">Los Pilares Astrológicos de Virgo</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-primary">🌍 Elemento: Tierra</h4>
                    <p className="text-muted-foreground">Enfoque en la practicidad, el orden y el trabajo. Virgo está conectado con la perfección material.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-secondary">🔄 Modalidad: Mutable</h4>
                    <p className="text-muted-foreground">Define la adaptación al servicio, la mejora continua y el detalle. Flexible en métodos, firme en calidad.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-accent">☿ Planeta Regente: Mercurio</h4>
                    <p className="text-muted-foreground">Rige la Mente Lógica, el Análisis y la Salud. El aspecto práctico de Mercurio.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">Perfil de Personalidad Virgo: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-lg mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Perfeccionismo:</strong> Búsqueda constante de la excelencia</li>
                      <li>• <strong>Metódico:</strong> Organización sistemática impecable</li>
                      <li>• <strong>Habilidad Manual:</strong> Destreza en trabajos detallados</li>
                      <li>• <strong>Servicio:</strong> Dedicación al bienestar ajeno</li>
                      <li>• <strong>Análisis:</strong> Capacidad crítica excepcional</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Criticismo:</strong> Juicio severo propio y ajeno</li>
                      <li>• <strong>Preocupación Excesiva:</strong> Ansiedad por detalles</li>
                      <li>• <strong>Timidez:</strong> Dificultad para brillar</li>
                      <li>• <strong>Hipocondría:</strong> Preocupación excesiva por la salud</li>
                      <li>• <strong>Rigidez:</strong> Dificultad con la espontaneidad</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-xl font-bold text-primary">Mantra Clave: "Yo Analizo"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">Virgo en el Amor: La Devoción del Cuidado Diario</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Virgo expresa amor a través del servicio y la atención a los detalles. Demuestra afecto cuidando la salud de su pareja, organizando su vida, y mostrando dedicación en los pequeños gestos diarios. Su amor es práctico, puro y dedicado.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita una pareja que aprecie sus esfuerzos y valore su forma única de cuidar. Puede ser reservado emocionalmente al principio, pero una vez comprometido, es increíblemente leal. Busca relaciones que mejoren su vida de manera tangible y que le permitan servir sin ser criticado por su naturaleza perfeccionista.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">Virgo en el Trabajo y Finanzas: Excelencia en el Detalle y la Organización</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Virgo destaca en campos que requieren precisión: contabilidad, salud, nutrición, edición, investigación científica, administración y cualquier profesión de servicio que demande excelencia técnica. Su ojo para el detalle lo hace invaluable en control de calidad.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Virgo es prudente y meticuloso. Lleva registros detallados, presupuestos cuidadosos y planifica a largo plazo. Ahorra de manera sistemática y invierte con investigación exhaustiva. Excelente para optimizar recursos y maximizar eficiencia financiera.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Virgo con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-lg mb-2">♉ Tauro y ♑ Capricornio</h4>
                    <p className="text-muted-foreground">
                      Los signos de tierra comparten valores de trabajo duro y resultados tangibles. Con Tauro hay estabilidad sensorial, mientras que con Capricornio existe una alianza poderosa basada en la ambición compartida y el respeto mutuo.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">♋ Cáncer y ♏ Escorpio</h4>
                    <p className="text-muted-foreground">
                      Los signos de agua complementan la naturaleza servicial de Virgo. Cáncer aporta nutrición emocional que Virgo necesita, mientras que Escorpio ofrece profundidad y transformación que desafía su perfeccionismo.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Virgo</h3>
                <p className="text-muted-foreground mb-4">
                  La aceptación de la imperfección es el gran desafío de Virgo. Piscis, su signo opuesto, le enseña sobre fe, fluidez y la belleza de lo imperfecto. Los signos de fuego (Aries, Leo, Sagitario) desafían su necesidad de control con espontaneidad y aventura.
                </p>
                <p className="text-muted-foreground">
                  Los signos de aire (Géminis, Libra, Acuario) pueden frustrar a Virgo con su aparente desorganización, pero le enseñan sobre flexibilidad mental y la importancia de las ideas sobre la ejecución perfecta.
                </p>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default HoroscopoVirgo;